// subjects/redux/questionBankSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchQuestionTypes,
  addQuestionThunk,
  editQuestionThunk,
  fetchQuestionsThunk,
  fetchQuestionVariantsThunk,
  editVariantThunk

} from "./questionBankThunk";

const storedQuestionTypeId = localStorage.getItem("selectedQuestionTypeId");

const questionBankSlice = createSlice({
  name: "questionBank",
  initialState: {
    questionTypes: [],
    selectedQuestionTypeId: storedQuestionTypeId || "",
    loadingQuestionTypes: false,
    errorQuestionTypes: null,
    editingQuestion: null,
    questions: [],
    totalPages: 20,
    loading: false,
    error: null,
    subQuestionVariants: null, 
    variantEditingQuestion: null,
    variantLoading: false,
    variantError: null,
    // You can add more states later like:
    // difficultyLevels: [],
    // chapters: [],
  },
  reducers: {
    setSelectedQuestionTypeId: (state, action) => {
      state.selectedQuestionTypeId = action.payload;
      localStorage.setItem("selectedQuestionTypeId", action.payload);
    },
    clearSelectedQuestionTypeId: (state) => {
      state.selectedQuestionTypeId = "";
      localStorage.removeItem("selectedQuestionTypeId");
    },
    setEditingQuestion: (state, action) => {
      state.editingQuestion = action.payload;
    },
    clearEditingQuestion: (state) => {
      state.editingQuestion = null;
    },
    addQuestion: (state, action) => {
      state.questions.push(action.payload);
    },
    updateQuestion: (state, action) => {
      const index = state.questions.findIndex(
        (q) => q.id === action.payload.id
      );
      if (index !== -1) {
        state.questions[index] = action.payload;
      }
    },
    addQuestionsFromFile: (state, action) => {
      state.questions = [...state.questions, ...action.payload];
    },
    clearSubQuestionVariants: (state) => {
      state.subQuestionVariants = [];
    },
     setVariantEditingQuestion: (state, action) => {
      state.variantEditingQuestion = action.payload;
    },
    clearVariantEditingQuestion: (state) => {
      state.variantEditingQuestion = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQuestionTypes.pending, (state) => {
        state.loadingQuestionTypes = true;
        state.errorQuestionTypes = null;
      })
      .addCase(fetchQuestionTypes.fulfilled, (state, action) => {
        state.loadingQuestionTypes = false;
        state.questionTypes = action.payload;

        // Validate current selection
        const isValid = action.payload.some(
          (type) => type.type_id.toString() === state.selectedQuestionTypeId
        );

        if (!isValid) {
          state.selectedQuestionTypeId = "";
          localStorage.removeItem("selectedQuestionTypeId");
        }
      })
      .addCase(fetchQuestionTypes.rejected, (state, action) => {
        state.loadingQuestionTypes = false;
        state.errorQuestionTypes = action.payload;
      })
      .addCase(addQuestionThunk.fulfilled, (state, action) => {
        state.questions.push(action.payload); // or re-fetch
      })
      .addCase(editQuestionThunk.fulfilled, (state, action) => {
        const idx = state.questions.findIndex(
          (q) => q.id === action.payload.id
        );
        if (idx !== -1) state.questions[idx] = action.payload;
      })
      .addCase(fetchQuestionsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.questions = [];
      })
      .addCase(fetchQuestionsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.questions = action.payload || [];
        const lastItem = action.payload[action.payload.length - 1];
        state.totalPages = lastItem?.pagination?.totalPages || 20;
       
        // Optional: Cache in localStorage
        if (action.payload?.length) {
          localStorage.setItem("questions", JSON.stringify(action.payload));
        } else {
          localStorage.removeItem("questions");
        }
      })
      .addCase(fetchQuestionsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
        state.questions = [];
        state.totalPages = null;
        localStorage.removeItem("questions");
      })
      .addCase(fetchQuestionVariantsThunk.pending, (state) => {
      state.variantLoading = true;
      state.variantError = null;
    })
    .addCase(fetchQuestionVariantsThunk.fulfilled, (state, action) => {
      state.variantLoading = false;
      state.subQuestionVariants = action.payload;  // ✅ Store full question object
    })
    .addCase(fetchQuestionVariantsThunk.rejected, (state, action) => {
      state.variantLoading = false;
      state.variantError = action.payload;
    })
  },
});

export const {
  setVariantEditingQuestion,
  clearVariantEditingQuestion,
  setSelectedQuestionTypeId,
  clearSelectedQuestionTypeId,
  clearSubQuestionVariants,
  setEditingQuestion,
  clearEditingQuestion,
  addQuestion,
  updateQuestion,
  addQuestionsFromFile,
} = questionBankSlice.actions;

export default questionBankSlice.reducer;
